import { z } from 'zod'
import { accountingChecksSettingsSchema, DEFAULT_ACCOUNTING_CHECKS_SETTINGS } from './accounting-health.js'
import { commissionSettingsSchema, DEFAULT_COMMISSION_SETTINGS } from './commissions.js'
import { optionalText, versionSchema } from './common.js'
import { DEFAULT_CONTRACTING_SETTINGS } from './contracting.js'
import type { ModuleKey, Permission } from './permissions.js'
import { DEFAULT_PROJECT_INSIGHTS_SETTINGS, projectInsightsSettingsSchema } from './project-insights.js'
import { DEFAULT_PROJECT_FEATURES, projectSettingsSchema } from './projects.js'
import { DEFAULT_TM_EXPENSE_MARKUP_PCT, DEFAULT_TM_GROUP_BY } from './projects-billing.js'
import {
  DEFAULT_BUDGET_CRITICAL_PCT,
  DEFAULT_BUDGET_WARN_PCT,
  DEFAULT_PROJECT_CATEGORY_DEFAULTS,
} from './projects-costs.js'
import { DEFAULT_PROJECT_TIME_SETTINGS } from './projects-time.js'
import type { PaymentMethod } from './sales.js'
import { serviceSettingsSchema } from './service.js'
import { contractSettingsSchema, DEFAULT_CONTRACT_SETTINGS } from './service-contracts.js'
import { taskSettingsSchema } from './tasks.js'
import { DEFAULT_WEEK_PATTERN, weekPatternSchema } from './working-calendar.js'

// ── setting groups (D6) ──────────────────────────────────────────────────────
//
// Small per-tenant settings stored as one JSON value per group (`tenant_setting_groups`), validated
// by the group's schema here. The typed `tenant_settings` row (sales settings, company profile) stays
// as it is. Group schemas carry no zod defaults: PATCH bodies are partial, and a default inside a
// partial schema would overwrite fields the request did not mention. Defaults live in `defaults`.
// A PATCH replaces each field it names as a whole (`terms` and `paymentDetails` included).

/** Optional text: trimmed; an empty string clears the field (null). */
const clearableText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((v) => (v ? v : null))

/**
 * The printed documents that may carry their own terms (settings Q1). Every other printed document
 * uses the default terms. A debit note prints the invoice's terms (it is an invoice of kind
 * DEBIT_NOTE); `purchase` is the purchase order.
 */
export const PRINT_DOCUMENT_TYPES = ['invoice', 'quotation', 'credit_note', 'receipt', 'purchase'] as const
export type PrintDocumentType = (typeof PRINT_DOCUMENT_TYPES)[number]

/**
 * Printed documents that take the print context (company block, branding, footer, payment details)
 * but carry no terms of their own: they print the default terms (ASM-072). The supplier vouchers and
 * the supplier return (pages/purchasing.md X-9, PU-67).
 */
export const DEFAULT_TERMS_PRINT_TYPES = ['supplier_payment', 'supplier_return', 'supplier_refund'] as const
export type DefaultTermsPrintType = (typeof DEFAULT_TERMS_PRINT_TYPES)[number]

/** Every `documentType` the print context accepts. */
export const PRINT_CONTEXT_DOCUMENT_TYPES = [...PRINT_DOCUMENT_TYPES, ...DEFAULT_TERMS_PRINT_TYPES] as const
export type PrintContextDocumentType = (typeof PRINT_CONTEXT_DOCUMENT_TYPES)[number]

/** An English and an Arabic text; a blank side is null (an empty string clears it). */
export interface BilingualText {
  en: string | null
  ar: string | null
}
const bilingualText = (max: number) => z.object({ en: optionalText(max), ar: optionalText(max) }).strict()

/** Terms of one document type (or the default): at most 2000 characters per language. */
export const TERMS_MAX_LENGTH = 2000
const termsText = bilingualText(TERMS_MAX_LENGTH)

/**
 * Per-document terms overrides (Q1). Every type is present; a null side uses the default's same side
 * (`termsDefault`), so an override can be English only. PATCH sends all five types.
 */
export const documentTermsSchema = z
  .object({
    invoice: termsText,
    quotation: termsText,
    credit_note: termsText,
    receipt: termsText,
    purchase: termsText,
  })
  .strict()
export type DocumentTerms = z.output<typeof documentTermsSchema>

/** How many bank accounts a document may list. */
export const PAYMENT_BANK_ACCOUNTS_MAX = 5

/**
 * Payment details printed on documents (Q2): BANK treasury accounts (bank, account number and IBAN
 * come from the account, so they stay correct) and a free-text line. The accounts must be active
 * BANK accounts when saved; one archived later is left off the print.
 */
export const paymentDetailsSchema = z
  .object({
    bankAccountIds: z
      .array(z.uuid().transform((v) => v.toLowerCase()))
      .max(PAYMENT_BANK_ACCOUNTS_MAX)
      .refine((ids) => new Set(ids).size === ids.length, 'Choose each bank account once'),
    text: bilingualText(500),
  })
  .strict()
export type PaymentDetailsSetting = z.output<typeof paymentDetailsSchema>

/**
 * When documents add the Umm al-Qura Hijri date beside the Gregorian one (ASM-135): never, only on
 * legal documents (contracts, offer letters, official letters: R1 onward), or on every document.
 */
export const HIJRI_DISPLAY_MODES = ['never', 'legal', 'always'] as const
export type HijriDisplayMode = (typeof HIJRI_DISPLAY_MODES)[number]

export const documentSettingsSchema = z.object({
  /** Printed at the foot of invoices and other documents. */
  printFooter: clearableText(500),
  printFooterAr: clearableText(500),
  /** NONE, or print a QR code of the document's public share link. */
  qrMode: z.enum(['NONE', 'PUBLIC_LINK']),
  /**
   * Days a new share link works for when the person sharing doesn't choose (they may still choose
   * "Never"); 0 = no expiry. A public page carries the customer's name, VAT number and address, so
   * links run out by default.
   */
  publicLinkDays: z.number().int().min(0).max(3650),
  /** Terms and conditions printed on every document unless its type has its own (Q1). */
  termsDefault: termsText,
  /** Overrides per document type; a null side falls back to `termsDefault`. */
  terms: documentTermsSchema,
  paymentDetails: paymentDetailsSchema,
  /** The Hijri date beside the Gregorian one on printed documents; `never` unless the admin sets it. */
  showHijri: z.enum(HIJRI_DISPLAY_MODES),
})

/** What a sale that takes stock below zero does (settings Q4, ASM-012). */
export const NEGATIVE_STOCK_POLICIES = ['ALLOW', 'WARN', 'BLOCK'] as const
export type NegativeStockPolicy = (typeof NEGATIVE_STOCK_POLICIES)[number]

export const inventorySettingsSchema = z.object({
  /**
   * ALLOW: posting and the till never mention stock. WARN (default): the invoice's stock check lists
   * shortages, posting goes ahead. BLOCK: posting an invoice or a till sale that would take a
   * variant below zero at its warehouse is refused (422 INSUFFICIENT_STOCK), inside the posting
   * transaction.
   */
  negativeStock: z.enum(NEGATIVE_STOCK_POLICIES),
})

export const EMAIL_DELIVERY_MODES = ['PLATFORM', 'SMTP'] as const
export type EmailDeliveryMode = (typeof EMAIL_DELIVERY_MODES)[number]
export const SMTP_SECURITY_MODES = ['STARTTLS', 'TLS'] as const

/** The ports a company's own mail server may use. */
export const SMTP_PORTS = [25, 465, 587, 2525] as const

/** Two or more DNS labels; the last (the top-level domain) has a letter, so an IP literal fails. */
const SMTP_HOST =
  /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*\.(?=[a-z0-9-]*[a-z])[a-z0-9]([a-z0-9-]*[a-z0-9])?$/

/** Names that never reach a public server (RFC 6761/6762/8375 and common private suffixes). */
const LOCAL_ONLY_SUFFIXES = ['localhost', 'local', 'internal', 'localdomain', 'home.arpa', 'lan', 'intranet']

export function isLocalOnlyHostName(host: string): boolean {
  const name = host.toLowerCase()
  return LOCAL_ONLY_SUFFIXES.some((suffix) => name === suffix || name.endsWith(`.${suffix}`))
}
export type SmtpSecurityMode = (typeof SMTP_SECURITY_MODES)[number]

/**
 * A plain object (no refine): `settingGroupPatchSchema` calls `.partial()`, which zod refuses on a
 * refined schema. The cross-field rule (SMTP needs a host, a user name and a sender address: 422
 * SMTP_INCOMPLETE) and the residency rule (422 ADAPTER_NOT_ALLOWED_IN_ZONE) are checked by the API on
 * the merged value. The SMTP password is the secret `email.smtpPassword`.
 */
export const notificationSettingsSchema = z.object({
  /** Sender name on e-mails sent through the platform relay (the address is the platform's). */
  fromName: clearableText(100),
  replyTo: z.email().max(254).nullable(),
  whatsappEnabled: z.boolean(),
  /** PLATFORM: the service's own relay. SMTP: the company's mail server below. */
  emailDelivery: z.enum(EMAIL_DELIVERY_MODES),
  /**
   * A public mail server's DNS name: at least two labels, the last not all digits (no IP literal), and
   * not a local-only name. The API also refuses a name that resolves to a private, loopback or
   * link-local address, at save (422 SMTP_HOST_NOT_ALLOWED) and again before each connection.
   */
  smtpHost: z
    .string()
    .trim()
    .toLowerCase()
    .max(253)
    .regex(SMTP_HOST, 'Enter a host name')
    .refine((host) => !isLocalOnlyHostName(host), 'Enter a public host name')
    .nullable(),
  /** The submission and relay ports only (no scanning of other services through the worker). */
  smtpPort: z
    .number()
    .int()
    .refine((port) => (SMTP_PORTS as readonly number[]).includes(port), 'Use port 25, 465, 587 or 2525'),
  smtpSecurity: z.enum(SMTP_SECURITY_MODES),
  smtpUsername: clearableText(254),
  smtpFromAddress: z.string().trim().toLowerCase().pipe(z.email().max(254)).nullable(),
  /**
   * The company confirms its own mail server is allowed for its data residency zone (an OM company may
   * use its own server only with this set).
   */
  smtpZoneApproved: z.boolean(),
})

/** An account a payment method starts on; null: none set. */
const methodAccount = z
  .uuid()
  .transform((v) => v.toLowerCase())
  .nullable()

export const paymentSettingsSchema = z.object({
  /**
   * The cash or bank account each payment method starts on in receipts, refunds and supplier
   * payments (legacy M04, settings G-S1), when the user has no account of their own for it. Each
   * must be an active account the method fits when saved; one archived later is skipped.
   */
  defaultAccounts: z
    .object({
      CASH: methodAccount,
      CARD: methodAccount,
      BANK_TRANSFER: methodAccount,
      CHEQUE: methodAccount,
      OTHER: methodAccount,
    } satisfies Record<PaymentMethod, z.ZodType>)
    .strict(),
})
/**
 * Price levels (ADR-045): the percent off Retail a level sells at when a product has no price of its own for
 * it (0: Retail). A price set on the product always wins.
 */
const levelDiscount = z.number().min(0).max(100)
export const pricingSettingsSchema = z.object({
  wholesaleDiscount: levelDiscount,
  distributorDiscount: levelDiscount,
})

/** The working week (ASM-141); holidays are rows of their own (calendar.ts). */
export const calendarSettingsSchema = z.object({ week: weekPatternSchema })

/**
 * The typefaces a company can give its app. Each one covers Latin and Arabic and is self-hosted
 * with the web app (no font CDN). `ibmPlex` is the design system's own face.
 */
export const APP_FONTS = ['ibmPlex', 'tajawal', 'cairo', 'almarai', 'notoSans', 'readexPro'] as const
export type AppFont = (typeof APP_FONTS)[number]

/** How large the app's text is, around the 14px body of the design system. */
export const APP_TEXT_SIZES = ['small', 'default', 'large', 'xlarge'] as const
export type AppTextSize = (typeof APP_TEXT_SIZES)[number]

/** The app's typeface and text size for everyone in the company (screens; printed documents keep theirs). */
export const typographySettingsSchema = z.object({
  font: z.enum(APP_FONTS),
  textSize: z.enum(APP_TEXT_SIZES),
})

export type MethodDefaultAccounts = z.output<typeof paymentSettingsSchema>['defaultAccounts']

interface SettingGroupDef<S extends z.ZodObject> {
  /** Who may read the group: any signed-in user, or holders of a permission. Writing needs settings.manage. */
  read: 'authenticated' | Permission
  schema: S
  defaults: z.output<S>
}
const group = <S extends z.ZodObject>(def: SettingGroupDef<S>) => def

const NO_TEXT: BilingualText = { en: null, ar: null }

export const SETTING_GROUPS = {
  /** Every printed document reads it. */
  documents: group({
    read: 'authenticated',
    schema: documentSettingsSchema,
    defaults: {
      printFooter: null,
      printFooterAr: null,
      qrMode: 'NONE',
      publicLinkDays: 90,
      termsDefault: NO_TEXT,
      terms: {
        invoice: NO_TEXT,
        quotation: NO_TEXT,
        credit_note: NO_TEXT,
        receipt: NO_TEXT,
        purchase: NO_TEXT,
      },
      paymentDetails: { bankAccountIds: [], text: NO_TEXT },
      showHijri: 'never',
    },
  }),
  notifications: group({
    read: 'settings.manage',
    schema: notificationSettingsSchema,
    defaults: {
      fromName: null,
      replyTo: null,
      whatsappEnabled: false,
      emailDelivery: 'PLATFORM',
      smtpHost: null,
      smtpPort: 587,
      smtpSecurity: 'STARTTLS',
      smtpUsername: null,
      smtpFromAddress: null,
      smtpZoneApproved: false,
    },
  }),
  /** Invoice forms and the till read the stock policy, so anyone signed in may. */
  inventory: group({
    read: 'authenticated',
    schema: inventorySettingsSchema,
    defaults: { negativeStock: 'WARN' },
  }),
  /** Task forms read the review default and the deadline rule, so anyone signed in may. */
  tasks: group({
    read: 'authenticated',
    schema: taskSettingsSchema,
    defaults: {
      reviewRequiredByDefault: false,
      assigneeMayChangeDueDate: false,
      escalateOverdueAfterDays: 1,
    },
  }),
  /** The technician's visit screen reads the approval and report rules, so anyone signed in may. */
  service: group({
    read: 'authenticated',
    schema: serviceSettingsSchema,
    defaults: {
      priceApproval: 'REQUIRED',
      reportPhotos: 'OPTIONAL',
      acknowledgment: 'OPTIONAL',
      assetRegister: true,
      assetChecklists: true,
      priceApprovalThreshold: null,
      visitLead: 'REQUIRED',
      followUpApproval: 'REQUIRED',
    },
  }),
  /**
   * Maintenance contracts (Service C2): notices, the plan horizon and the new contract's defaults. The
   * contract form and the job's coverage words read it, so anyone signed in may.
   */
  contracts: group({
    read: 'authenticated',
    schema: contractSettingsSchema,
    defaults: DEFAULT_CONTRACT_SETTINGS,
  }),
  /**
   * Projects' functions and defaults (ADR-039). Document forms read the switches (project pickers,
   * columns), so anyone signed in may.
   */
  projects: group({
    read: 'authenticated',
    schema: projectSettingsSchema,
    defaults: {
      features: DEFAULT_PROJECT_FEATURES,
      defaultTypeId: null,
      progressMethod: 'MILESTONES',
      categoryDefaults: DEFAULT_PROJECT_CATEGORY_DEFAULTS,
      contracting: DEFAULT_CONTRACTING_SETTINGS,
      tmExpenseMarkupPct: DEFAULT_TM_EXPENSE_MARKUP_PCT,
      tmGroupBy: DEFAULT_TM_GROUP_BY,
      budgetWarnPct: DEFAULT_BUDGET_WARN_PCT,
      budgetCriticalPct: DEFAULT_BUDGET_CRITICAL_PCT,
      time: DEFAULT_PROJECT_TIME_SETTINGS,
    },
  }),
  /** AA1 Books health: which checks run and their thresholds (ACCOUNTING_ASSISTANT.md §3.1). */
  accountingChecks: group({
    read: 'ledger.read',
    schema: accountingChecksSettingsSchema,
    defaults: DEFAULT_ACCOUNTING_CHECKS_SETTINGS,
  }),
  /** Signal thresholds (projects/05 §4.3): read by everyone who sees projects. */
  projectInsights: group({
    read: 'projects.read',
    schema: projectInsightsSettingsSchema,
    defaults: DEFAULT_PROJECT_INSIGHTS_SETTINGS,
  }),
  /** Every payment form reads the default accounts, so anyone signed in may. */
  payments: group({
    read: 'authenticated',
    schema: paymentSettingsSchema,
    defaults: {
      defaultAccounts: { CASH: null, CARD: null, BANK_TRANSFER: null, CHEQUE: null, OTHER: null },
    },
  }),
  /** Due dates and working-day timers read the week, so anyone signed in may. */
  calendar: group({
    read: 'authenticated',
    schema: calendarSettingsSchema,
    defaults: { week: DEFAULT_WEEK_PATTERN },
  }),
  /** Commission (ADR-049): maker-checker and payment through payroll; read by those who see commission. */
  commissions: group({
    read: 'commissions.read',
    schema: commissionSettingsSchema,
    defaults: DEFAULT_COMMISSION_SETTINGS,
  }),
  /** Document forms and the till price lines with it, so anyone signed in may read it. */
  pricing: group({
    read: 'authenticated',
    schema: pricingSettingsSchema,
    defaults: { wholesaleDiscount: 0, distributorDiscount: 0 },
  }),
  /** Every screen applies the company's typeface and text size, so anyone signed in may read it. */
  typography: group({
    read: 'authenticated',
    schema: typographySettingsSchema,
    defaults: { font: 'ibmPlex', textSize: 'default' },
  }),
}

export type SettingGroup = keyof typeof SETTING_GROUPS
export type SettingGroupValue<G extends SettingGroup> = z.output<(typeof SETTING_GROUPS)[G]['schema']>
export type DocumentSettings = SettingGroupValue<'documents'>
export type NotificationSettings = SettingGroupValue<'notifications'>
export type InventorySettings = SettingGroupValue<'inventory'>
export type PaymentSettings = SettingGroupValue<'payments'>
export type TaskSettingsValue = SettingGroupValue<'tasks'>
export type ServiceSettingsValue = SettingGroupValue<'service'>
export type ContractSettingsValue = SettingGroupValue<'contracts'>
export type ProjectSettingsValue = SettingGroupValue<'projects'>
export type ProjectInsightsSettingsValue = SettingGroupValue<'projectInsights'>
export type CalendarSettings = SettingGroupValue<'calendar'>
export type TypographySettings = SettingGroupValue<'typography'>
export type PricingSettings = SettingGroupValue<'pricing'>
export type CommissionSettings = SettingGroupValue<'commissions'>

export function isSettingGroup(value: string): value is SettingGroup {
  return Object.hasOwn(SETTING_GROUPS, value)
}

/** PATCH /settings/:group: any subset of the group's fields plus the version read. Unknown fields are refused. */
export function settingGroupPatchSchema<G extends SettingGroup>(name: G) {
  return SETTING_GROUPS[name].schema.partial().extend({ version: versionSchema }).strict()
}

/** GET and PATCH /settings/:group. A group never saved shows its defaults at version 1. */
export interface SettingGroupState<G extends SettingGroup = SettingGroup> {
  group: G
  value: SettingGroupValue<G>
  version: number
  updatedAt: string | null
  updatedBy: string | null
  /**
   * `notifications` only, computed by the server and read-only: e-mail can be sent (the platform relay
   * is configured, or the company's SMTP server is complete and allowed in its zone).
   */
  emailReady?: boolean
}

// ── tenant secrets (D7) ──────────────────────────────────────────────────────

/**
 * The only secrets a tenant can store (the table is not a blob store). Values are write-only: the API
 * encrypts them and never returns them; only the worker decrypts.
 */
export const SECRET_NAMES = {
  'whatsapp.apiKey': { en: 'WhatsApp API key', ar: 'مفتاح واجهة واتساب', maxLength: 512 },
  'email.smtpPassword': { en: 'SMTP password', ar: 'كلمة مرور خادم البريد (SMTP)', maxLength: 512 },
  /**
   * P8: the company's own OpenAI key (05 §10). Read in the API only through `ai_tenant_key()` at run start
   * (the second D9 exception, ADR-041); `ai.manage` may set or remove it besides settings.manage_secrets.
   */
  'ai.openaiApiKey': {
    en: 'OpenAI API key',
    ar: 'مفتاح واجهة OpenAI',
    maxLength: 512,
    managedBy: 'ai.manage',
  },
} as const satisfies Record<string, { en: string; ar: string; maxLength: number; managedBy?: Permission }>

/** Who may set or remove a secret besides settings.manage_secrets (the routes and service check per name). */
export function secretManagedBy(name: SecretName): Permission | null {
  const def = SECRET_NAMES[name]
  return 'managedBy' in def ? def.managedBy : null
}

export type SecretName = keyof typeof SECRET_NAMES

export function isSecretName(value: string): value is SecretName {
  return Object.hasOwn(SECRET_NAMES, value)
}

/** PUT /settings/secrets/:name. The value is never echoed back or logged. */
export const secretValueSchema = z.object({ value: z.string().min(1).max(4096) })
export type SecretValue = z.input<typeof secretValueSchema>

/** GET /settings/secrets lists every registered name, set or not. */
export interface SecretStatus {
  name: SecretName
  isSet: boolean
  updatedAt: string | null
  /** User id of whoever last set it. */
  updatedBy: string | null
}

// ── print context (settings G-D1, G-D2, G-D4, G-C2) ─────────────────────────

/** GET /settings/print-context?documentType=… */
export const printContextQuerySchema = z.object({ documentType: z.enum(PRINT_CONTEXT_DOCUMENT_TYPES) })
export type PrintContextQuery = z.input<typeof printContextQuerySchema>

/** A bank account as documents print it (from the treasury account). */
export interface PrintBankAccount {
  id: string
  name: string
  nameAr: string | null
  bankName: string | null
  accountNumber: string | null
  iban: string | null
}

/** The payment details a document prints: the chosen bank accounts (active BANK ones) and the text. */
export interface PrintPaymentDetails {
  bankAccounts: PrintBankAccount[]
  text: BilingualText
}

/**
 * Everything a printed document of one type needs from the settings, resolved: one call per print
 * page. Terms are the type's override where set, per language, else the default.
 */
export interface PrintContext {
  documentType: PrintContextDocumentType
  company: {
    legalName: string | null
    legalNameAr: string | null
    /** The name shown in the app (the tenant's name); a print falls back to it without a legal name. */
    displayName: string
    displayNameAr: string | null
    vatNumber: string | null
    crNumber: string | null
    address: string | null
    addressAr: string | null
    phone: string | null
    email: string | null
  }
  /**
   * The images' file ids (null when not set), served by `GET /settings/company/logo|signature|stamp`;
   * an id changes on every replacement, so it also busts caches. The signature and the stamp print on
   * posted documents only, never on drafts or void documents (Q3): the print page applies the rule
   * from the document's status (a quotation has no draft: it prints them unless REJECTED).
   */
  branding: {
    logoFileId: string | null
    signatureFileId: string | null
    stampFileId: string | null
  }
  footer: BilingualText
  terms: BilingualText
  paymentDetails: PrintPaymentDetails
  qrMode: DocumentSettings['qrMode']
}

// ── modules (settings G-M1) ──────────────────────────────────────────────────

/**
 * GET /settings/modules: each module the company can be entitled to (the permission groups other
 * than administration), whether it is on, and an i18n key for a one-line description of what it
 * contains (`modules.<key>.description`). Modules change only through the operator (D15).
 */
export interface ModuleStatus {
  key: ModuleKey
  enabled: boolean
  name: { en: string; ar: string }
  descriptionKey: string
}

// ── numbering overview (settings G-D6) ──────────────────────────────────────

/**
 * GET /numbering/series: every number series, read-only (settings Q5). `pattern` shows the format
 * (`INV-000`, `PAY-YYYY-0000`); `nextNumber` is the number the next document gets (this year's for a
 * series that restarts every year); `inUse` is false for a series that has never numbered anything.
 * A series found in the database but unknown to the API (none today) has a null pattern.
 */
export interface NumberSeriesInfo {
  series: string
  name: { en: string; ar: string }
  pattern: string | null
  resetsYearly: boolean
  /**
   * Counts per project (contracting's `project_variation` VO-001 and `project_claim` IPC-01, 04 §6): each
   * project starts at 1, so `nextNumber` shows a project's first number. Optional for older fixtures.
   */
  perProject?: boolean
  /** The period the next number belongs to: the current year for yearly series, else ''. */
  period: string
  nextValue: number
  nextNumber: string | null
  inUse: boolean
}
